import produce from "immer";
import { create } from "zustand";
import * as api from "../services/inspections";
import { arrayToByIdsAllIds } from "./utils";

export const makePageKey = (params) =>
  `${params.from}|${params.to}|${params.inspector ?? ""}|${params.device_type_id ?? ""}|${params.converted ?? ""}|${params.sort_by}|${params.sort_dir}|${params.limit}|${params.offset}`;

export const makeDeviceTypesKey = (params) =>
  `${params.from}|${params.to}|${params.inspector ?? ""}`;

export const makeMapKey = (params) =>
  `${params.from}|${params.to}|${Array.isArray(params.inspector) ? params.inspector.join(",") : params.inspector ?? ""}|${params.device_type_id ?? ""}`;


const stampIds = (items) =>
  items.map((item) => ({
    ...item,
    _id: item.slug,
  }));

export const useInspectionsStore = create((set, get) => ({
  byIds: {},
  pageCache: {},
  allIds: [],
  total: 0,
  lastFetchParams: null,
  isLoading: false,
  isLoadingCard: false,
  mapData: [],
  mapStats: { total: 0, located: 0, unlocated: 0 },
  mapLoading: false,
  mapError: null,
  mapKey: null,
  deviceTypes: [],
  deviceTypesKey: null,

  fetchInspections: async (params) => {
    const key = makePageKey(params);
    if (get().pageCache[key]) return;

    set(
      produce((draft) => {
        draft.isLoading = true;
      }),
    );
    const result = await api.getInspections(params);
    const stamped = stampIds(result.data || []);
    const [byIds, allIds] = arrayToByIdsAllIds(stamped, "_id");
    set(
      produce((draft) => {
        draft.byIds = { ...draft.byIds, ...byIds };
        draft.pageCache[key] = { allIds, total: result.total || 0 };
        draft.allIds = allIds;
        draft.total = result.total || 0;
        draft.lastFetchParams = params;
        draft.isLoading = false;
      }),
    );
    return result;
  },

  fetchInspectionsMap: async (params) => {
    const key = makeMapKey(params);
    if (get().mapKey === key && !get().mapError) return;

    set(
      produce((draft) => {
        draft.mapLoading = true;
        draft.mapError = null;
      }),
    );
    const result = await api.getInspectionsMap(params);
    set(
      produce((draft) => {
        if (result.error) {
          draft.mapError = result.error;
          draft.mapData = [];
          draft.mapStats = { total: 0, located: 0, unlocated: 0 };
        } else {
          draft.mapData = result.data || [];
          draft.mapStats = {
            total: result.total || 0,
            located: result.located || 0,
            unlocated: result.unlocated || 0,
          };
          draft.mapKey = key;
        }
        draft.mapLoading = false;
      }),
    );
    return result;
  },

  fetchDeviceTypes: async (params) => {
    const key = makeDeviceTypesKey(params);
    if (get().deviceTypesKey === key) return;

    const result = await api.getInspectionDeviceTypes(params);
    if (result.error) return result;
    set(
      produce((draft) => {
        draft.deviceTypes = result.data || [];
        draft.deviceTypesKey = key;
      }),
    );
    return result;
  },

  fetchInspectionCard: async (slug) => {
    if (get().byIds[slug]?._cardLoaded) return;

    set(
      produce((draft) => {
        draft.isLoadingCard = true;
      }),
    );
    const result = await api.getInspectionCard(slug);
    const cardData = Object.fromEntries(
      Object.entries(result.data || {}).filter(([, v]) => v != null),
    );
    set(
      produce((draft) => {
        draft.byIds[slug] = { ...draft.byIds[slug], ...cardData, _id: slug, _cardLoaded: true };
        draft.isLoadingCard = false;
      }),
    );
    return result;
  },

  // No showApiError here: the convert wizard renders errors inline per row.
  convertToWorkOrder: async (slug, { assignedToId }) => {
    const result = await api.convertInspection(slug, {
      assigned_to_id: assignedToId,
    });
    if (result.data) {
      set(
        produce((draft) => {
          draft.byIds[slug] = { ...draft.byIds[slug], work_order_id: result.data.id };
        }),
      );
    }
    return result;
  },
}));
